import React from "react";
import { Link, useLocation } from "react-router";
import {
  Users,
  Megaphone,
  ChartLine,
  ChevronRight,
  ChevronDown,
  Menu,
  Package,
  Receipt,
  SquareArrowOutUpRightIcon,
  Crown,
  FileText
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AccessModule } from "@shoutout-labs/market_buzz_crm_types";
import { usePermissions } from "@/hooks/usePermissions";
import FeatureUtils from "@/utility/FeatureUtils";

interface NavigationSubItem {
  name: string;
  href: string;
  icon?: React.ComponentType<any>;
  premium?: boolean;
  requiredModule?: AccessModule;
  requiredFeature?: string;
  subItems?: NavigationSubItem[];
}

interface NavigationItem {
  name: string;
  href: string;
  premium?: boolean;
  requiredModule?: AccessModule;
  requiredFeature?: string;
  subItems?: NavigationSubItem[];
}

const navigation: NavigationItem[] = [
  {
    name: "DASHBOARD",
    href: "/",
    requiredModule: AccessModule.DASHBOARDS,
    requiredFeature: "DASHBOARD_FULL_VIEW"
  },
  {
    name: "INSIGHTS",
    href: "/insight-unlock",
    subItems: [
      {
        name: "Sales Reports",
        href: "/reports/sales",
        icon: FileText,
        requiredModule: AccessModule.SALES_REPORTS,
        subItems: [
          {
            name: "Overview",
            href: "/reports/sales/overview",
            icon: Users,
            requiredFeature: "SALES_REPORT_OVERVIEW"
          },
          {
            name: "Trends & Patterns",
            href: "/reports/sales/trends",
            icon: ChartLine,
            premium: true,
            requiredFeature: "SALES_REPORTS_FULL_VIEW"
          },
          {
            name: "Product Insights",
            href: "/reports/sales/products",
            icon: Package,
            premium: true,
            requiredFeature: "SALES_REPORTS_FULL_VIEW"
          }
        ]
      },
      {
        name: "Customer Reports",
        href: "/reports/customers",
        icon: FileText,
        requiredModule: AccessModule.CUSTOMERS_REPORTS,
        subItems: [
          {
            name: "Overview",
            href: "/reports/customers/overview",
            icon: Users,
            requiredFeature: "CUSTOMER_REPORT_OVERVIEW"
          },
          {
            name: "Behavior & Engagement",
            href: "/reports/customers/behavior",
            icon: ChartLine,
            premium: true,
            requiredFeature: "CUSTOMERS_REPORTS_FULL_VIEW"
          },
          {
            name: "Purchase Pattern Analysis",
            href: "/reports/customers/purchase-pattern",
            icon: Package,
            premium: true,
            requiredFeature: "CUSTOMERS_REPORTS_FULL_VIEW"
          }
        ]
      }
    ]
  },
  {
    name: "CRM",
    href: "/crm",
    premium: true,
    subItems: [
      {
        name: "Customers",
        href: "/customers",
        icon: Users,
        requiredModule: AccessModule.CUSTOMERS
      },
      {
        name: "Automated Campaigns",
        href: "/automated-campaigns",
        icon: SquareArrowOutUpRightIcon,
        requiredModule: AccessModule.AUTOMATED_CAMPAIGNS
      },
      {
        name: "SMS Campaigns",
        href: "/campaigns",
        icon: Megaphone,
        requiredModule: AccessModule.BROADCAST_CAMPAIGNS,
        subItems: [
          // { name: "Campaign Ideas", href: "/campaigns/sms" },
          {
            name: "Campaign Ideas",
            href: "/campaigns/broadcast"
          },
          {
            name: "Campaign History",
            href: "/campaigns/report",
            requiredModule: AccessModule.BROADCAST_CAMPAIGNS_REPORTS
          }
        ]
      },
      {
        name: "Products",
        href: "/products",
        icon: Package,
        requiredModule: AccessModule.PRODUCTS
      },
      {
        name: "Transactions",
        href: "/transactions",
        icon: Receipt,
        requiredModule: AccessModule.TRANSACTIONS
      }
    ]
  }
];

interface SidebarProps {
  isCollapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
  onMobileToggle?: () => void;
}

export function Sidebar({
  isCollapsed: externalIsCollapsed,
  onCollapseChange,
  onMobileToggle
}: SidebarProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const [internalIsCollapsed, setInternalIsCollapsed] = React.useState(false);
  const [expandedItems, setExpandedItems] = React.useState<string[]>(() => {
    // Start expanded for Insights and CRM so reports and CRM sections are visible by default
    return ["INSIGHTS", "CRM"];
  });

  const {
    permissions,
    modules: userModules,
    features: userFeatures,
    isLoading: permissionsLoading,
    hasError
  } = usePermissions();

  const isCollapsed =
    externalIsCollapsed !== undefined
      ? externalIsCollapsed
      : internalIsCollapsed;

  const toggleSidebar = () => {
    const newCollapsedState = !isCollapsed;
    if (onCollapseChange) onCollapseChange(newCollapsedState);
    else setInternalIsCollapsed(newCollapsedState);
  };

  const toggleExpand = (name: string) => {
    setExpandedItems((prev) =>
      prev.includes(name)
        ? prev.filter((item) => item !== name)
        : [...prev, name]
    );
  };

  // 🔧 Fixed access logic for all user types
  const hasAccessToItem = (
    item: NavigationItem | NavigationSubItem
  ): boolean => {
    // Handle required features
    if (item.requiredFeature) {
      // Hierarchical access
      if (
        item.requiredFeature === "SALES_REPORT_OVERVIEW" &&
        userFeatures.includes("SALES_REPORTS_FULL_VIEW")
      )
        return true;
      if (
        item.requiredFeature === "CUSTOMER_REPORT_OVERVIEW" &&
        userFeatures.includes("CUSTOMERS_REPORTS_FULL_VIEW")
      )
        return true;

      const reportFeatures = [
        "SALES_REPORT_OVERVIEW",
        "SALES_REPORTS_FULL_VIEW",
        "CUSTOMER_REPORT_OVERVIEW",
        "CUSTOMERS_REPORTS_FULL_VIEW"
      ];

      // ✅ CRM users — show all report tabs (locked visible)
      if (
        permissions?.appType === "CRM" &&
        reportFeatures.includes(item.requiredFeature)
      ) {
        return true;
      }

      // ✅ Analytics users — also show all report tabs (locked visible)
      if (
        permissions?.appType === "ANALYTICS" &&
        reportFeatures.includes(item.requiredFeature)
      ) {
        return true;
      }

      return userFeatures.includes(item.requiredFeature);
    }

    // Handle required modules
    if (item.requiredModule) {
      const crmModules = [
        AccessModule.CUSTOMERS,
        AccessModule.PRODUCTS,
        AccessModule.TRANSACTIONS,
        AccessModule.AUTOMATED_CAMPAIGNS,
        AccessModule.BROADCAST_CAMPAIGNS,
        AccessModule.BROADCAST_CAMPAIGNS_REPORTS
      ];

      // ANALYTICS users can see CRM modules (locked view)
      if (
        permissions?.appType === "ANALYTICS" &&
        crmModules.includes(item.requiredModule)
      )
        return true;

      return userModules.includes(item.requiredModule);
    }

    return true;
  };

  // Filter navigation based on access
  const filteredNavigation = React.useMemo(() => {
    if (permissionsLoading || hasError) return navigation;

    const filterItems = (
      items: (NavigationItem | NavigationSubItem)[]
    ): (NavigationItem | NavigationSubItem)[] =>
      items
        .filter((item) => {
          if (!hasAccessToItem(item)) return false;
          if (item.subItems && item.subItems.length > 0) {
            return item.subItems.some((sub) => hasAccessToItem(sub));
          }
          return true;
        })
        .map((item) => {
          if (item.subItems) {
            return { ...item, subItems: filterItems(item.subItems) };
          }
          return item;
        });

    return filterItems(navigation) as NavigationItem[];
  }, [permissionsLoading, hasError, userModules, userFeatures, permissions]);

  React.useEffect(() => {
    filteredNavigation.forEach((item) => {
      if (
        item.subItems &&
        pathname.startsWith(item.href) &&
        item.href !== "/"
      ) {
        setExpandedItems((prev) =>
          prev.includes(item.name) ? prev : [...prev, item.name]
        );
      }
    });
  }, [pathname, filteredNavigation]);

  const handleNavigationClick = () => {
    if (onMobileToggle && window.innerWidth < 1024) onMobileToggle();
  };

  const renderSubItems = (
    subItems: NavigationSubItem[],
    parentName: string,
    level: number = 0
  ) => {
    return subItems.map((subItem) => {
      const isSubItemActive =
        pathname === subItem.href || pathname.startsWith(`${subItem.href}/`);
      const hasNestedSubItems = subItem.subItems && subItem.subItems.length > 0;
      const isNestedExpanded = expandedItems.includes(
        `${parentName}-${subItem.name}`
      );

      return (
        <div key={subItem.name}>
          {hasNestedSubItems ? (
            <div>
              <button
                onClick={() => toggleExpand(`${parentName}-${subItem.name}`)}
                className={cn(
                  "flex w-full items-center justify-between rounded-md py-2 pr-3 text-sm font-medium",
                  isSubItemActive
                    ? "bg-blue-50 text-blue-600"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                )}
                style={{ paddingLeft: `${(level + 1) * 24}px` }}
              >
                <div className="flex items-center">
                  {subItem.icon && (
                    <subItem.icon
                      className={cn(
                        "mr-3 h-4 w-4",
                        isSubItemActive ? "text-blue-600" : "text-gray-400"
                      )}
                    />
                  )}
                  <span>{subItem.name}</span>
                </div>
                <div className="flex items-center">
                  {subItem.premium && (
                    <Crown className="h-3 w-3 text-yellow-500 mr-2" />
                  )}
                  {isNestedExpanded ? (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-500" />
                  )}
                </div>
              </button>
              {isNestedExpanded && (
                <div className="mt-1 space-y-1">
                  {renderSubItems(
                    subItem.subItems!,
                    `${parentName}-${subItem.name}`,
                    level + 1
                  )}
                </div>
              )}
            </div>
          ) : (
            <Link
              to={subItem.href}
              onClick={handleNavigationClick}
              className={cn(
                "flex items-center justify-between rounded-md py-2 pr-3 text-sm font-medium",
                isSubItemActive
                  ? "bg-blue-50 text-blue-600"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              )}
              style={{ paddingLeft: `${(level + 1) * 24}px` }}
            >
              <div className="flex items-center">
                {subItem.icon && (
                  <subItem.icon
                    className={cn(
                      "mr-3 h-4 w-4",
                      isSubItemActive ? "text-blue-600" : "text-gray-400"
                    )}
                  />
                )}
                <span>{subItem.name}</span>
              </div>
              <div className="flex items-center">
                {subItem.premium && (
                  <Crown className="h-3 w-3 text-yellow-500" />
                )}
              </div>
            </Link>
          )}
        </div>
      );
    });
  };

  return (
    <div
      className={cn(
        "flex flex-col bg-white border-r border-gray-200 transition-all duration-300",
        isCollapsed ? "w-16" : "w-64"
      )}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        {!isCollapsed && (
          <div className="flex items-center">
            <img
              src="/logo-sm.png"
              alt="Market Buzz Logo"
              className="w-8 h-8 mr-3"
            />
            <span className="text-lg font-bold text-gray-900">MARKETBUZZ</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          {onMobileToggle && (
            <button
              onClick={onMobileToggle}
              className="lg:hidden p-2 rounded-md hover:bg-gray-100"
              aria-label="Close sidebar"
            >
              <ChevronRight className="h-5 w-5 text-gray-500" />
            </button>
          )}
          <button
            onClick={toggleSidebar}
            className="hidden lg:block p-1 rounded-md hover:bg-gray-100"
            aria-label="Toggle sidebar"
          >
            <Menu className="h-5 w-5 text-gray-500" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-4">
        <nav className="space-y-1 px-2">
          {permissionsLoading && !hasError && (
            <div className="flex items-center justify-center p-4">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              {!isCollapsed && (
                <span className="ml-2 text-sm text-gray-600">Loading...</span>
              )}
            </div>
          )}
          {filteredNavigation.map((item) => {
            const isActive =
              pathname === item.href || pathname.startsWith(`${item.href}/`);
            const isExpanded = expandedItems.includes(item.name);
            const hasSubItems = item.subItems && item.subItems.length > 0;

            return (
              <div key={item.name}>
                {hasSubItems ? (
                  <div>
                    <button
                      onClick={() => toggleExpand(item.name)}
                      className={cn(
                        "flex w-full items-center justify-between rounded-md px-3 py-3 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-gray-100 text-gray-900"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      )}
                    >
                      <div className="flex items-center">
                        {!isCollapsed && <span>{item.name}</span>}
                      </div>
                      {!isCollapsed && (
                        <div className="flex items-center">
                          {item.premium &&
                            (item.name === "CRM" ? (
                              !FeatureUtils.isCRMEnabled(permissions) ? (
                                <span className="text-xs font-medium text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
                                  Install
                                </span>
                              ) : null
                            ) : (
                              permissions?.appType !== "ANALYTICS" && (
                                <Crown className="h-3 w-3 text-yellow-500" />
                              )
                            ))}
                        </div>
                      )}
                    </button>
                    {isExpanded && !isCollapsed && (
                      <div className="mt-1 space-y-1">
                        {renderSubItems(item.subItems!, item.name)}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    onClick={handleNavigationClick}
                    className={cn(
                      "flex items-center justify-between rounded-md px-3 py-3 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-gray-100 text-gray-900"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    )}
                  >
                    <div className="flex items-center">
                      {!isCollapsed && <span>{item.name}</span>}
                    </div>
                    {!isCollapsed &&
                      item.premium &&
                      (item.name === "CRM" ? (
                        !FeatureUtils.isCRMEnabled(permissions) ? (
                          <span className="text-xs font-medium text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
                            {permissions?.appType === "ANALYTICS"
                              ? "Buy Credits"
                              : "Upgrade"}
                          </span>
                        ) : null
                      ) : (
                        permissions?.appType !== "ANALYTICS" && (
                          <Crown className="h-3 w-3 text-yellow-500" />
                        )
                      ))}
                  </Link>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
